import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ChevronRight, LibraryBig } from "lucide-react";
import { toast } from "sonner";
import FormCard from "@/components/forms/FormCard";
import CatalogPanel, { CatalogFilters, applyFilters } from "@/components/forms/CatalogPanel";
import MyRecords from "@/components/forms/MyRecords";

const EMPTY_FILTERS = { q: "", category: "all", audience: "all", source: "all", status: "all" };
const FEATURE_RANK = { existing: 0, approved: 0, beta: 0, draft: 1, government: 2, retired: 3, catalog_only: 3 };

// LUMIS HR reviewers only: quick view of governed templates and their state
function GovernanceStrip({ entries }) {
  const governed = entries.filter((e) => e.template_id?.startsWith("draft-"));
  const seen = new Set();
  const uniq = governed.filter((e) => !seen.has(e.template_id) && seen.add(e.template_id));
  const counts = uniq.reduce((a, e) => ({ ...a, [e.status]: (a[e.status] || 0) + 1 }), {});
  return (
    <Card data-testid="governance-strip" className="p-3 mb-4 bg-[#F5F9FE] border-[#0A2A5E]/30">
      <div className="text-xs font-bold uppercase tracking-wider text-[#0A2A5E]">LUMIS HR governance (reserved for a later phase) · {counts.beta || 0} beta · {counts.draft || 0} draft · {counts.approved || 0} approved · {counts.retired || 0} retired</div>
      <ul className="mt-2 flex flex-wrap gap-1.5">
        {uniq.map((e) => (
          <li key={e.template_id}><Link to={`/app/forms/fill/${e.template_id}`} data-testid={`gov-link-${e.template_id}`} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border ${e.status === "approved" ? "border-emerald-500 text-emerald-800 bg-emerald-50" : e.status === "retired" ? "border-slate-400 text-slate-700 bg-slate-100" : "border-[#F5A623] text-[#7C3F00] bg-[#FDECD0]"}`}>{e.title} · {e.version}</Link></li>
        ))}
      </ul>
    </Card>
  );
}

// Managers set the organization name used on templates and PDFs ("Prepared for use by")
function OrgNameEditor({ current, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(current === "your organization" ? "" : current);
  const save = async () => {
    try { const { data } = await api.put("/settings/org-name", { name }); onSaved(data.name); setEditing(false); toast.success("Organization name saved"); }
    catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not save"); }
  };
  if (!editing) return <button type="button" data-testid="edit-org-name-btn" onClick={() => setEditing(true)} className="ml-2 text-xs underline text-[#0A2A5E]">Edit organization name</button>;
  return (
    <span className="inline-flex items-center gap-2 ml-2">
      <input data-testid="org-name-input" aria-label="Organization name" value={name} onChange={(e) => setName(e.target.value)} className="h-8 px-2 text-sm border border-slate-300 rounded-md" placeholder="Organization name" />
      <Button size="sm" data-testid="org-name-save-btn" onClick={save} className="h-8 bg-[#0A2A5E] text-white">Save</Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-8">Cancel</Button>
    </span>
  );
}

const isUpcoming = (e) => e.status === "catalog_only" || e.status === "retired";

function featuredFor(entries, catId) {
  return entries.filter((e) => e.category_id === catId).sort((a, b) => FEATURE_RANK[a.status] - FEATURE_RANK[b.status]).slice(0, 3);
}

function CategorySection({ cat, entries, upcomingCount = 0, isManager, labels, onViewAll }) {
  const featured = featuredFor(entries, cat.id);
  const total = entries.filter((e) => e.category_id === cat.id).length;
  if (!total && !upcomingCount) return null;
  return (
    <section data-testid={`section-${cat.id}`} aria-labelledby={`h-${cat.id}`} className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 id={`h-${cat.id}`} className="font-display text-base md:text-lg font-bold text-[#0A2A5E]">{cat.label}</h2>
          <p className="text-xs text-slate-500">{total ? `${featured.length} featured of ${total} forms` : "No forms available yet"}{upcomingCount ? ` · ${upcomingCount} planned` : ""}</p>
        </div>
        <Button variant="ghost" size="sm" data-testid={`view-all-${cat.id}`} onClick={() => onViewAll(cat.id)} className="text-[#0A2A5E] hover:bg-[#FDECD0] h-8">
          View all in {cat.label.split(",")[0].split(" and ")[0]} <ChevronRight className="w-4 h-4 ml-1" aria-hidden="true" />
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {featured.map((e) => <FormCard key={e.id} entry={e} isManager={isManager} labels={labels} />)}
      </div>
    </section>
  );
}

function UpcomingSection({ upcoming, categories, isManager, labels }) {
  return (
    <section data-testid="upcoming-forms-section" aria-labelledby="h-upcoming" className="space-y-5 pt-2">
      <div>
        <h2 id="h-upcoming" className="font-display text-base md:text-lg font-bold text-[#0A2A5E]">Planned forms ({upcoming.length})</h2>
        <p className="text-[11px] text-slate-500 mt-1" data-testid="upcoming-forms-note">Planned forms are catalog entries pending LUMIS HR review and not yet available. They cannot be opened, completed, submitted or printed.</p>
      </div>
      {categories.map((c) => {
        const list = upcoming.filter((e) => e.category_id === c.id);
        if (!list.length) return null;
        return (
          <div key={c.id} data-testid={`upcoming-${c.id}`} className="space-y-2">
            <h3 className="text-sm font-bold text-[#0A2A5E]">{c.label} <span className="text-xs font-normal text-slate-500">· {list.length} planned</span></h3>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">{list.map((e) => <FormCard key={e.id} entry={e} isManager={isManager} labels={labels} />)}</div>
          </div>
        );
      })}
    </section>
  );
}

export default function Forms() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [center, setCenter] = useState(null);
  const [subs, setSubs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [tab, setTab] = useState("center");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [openCats, setOpenCats] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);

  const loadSubs = () => api.get("/forms/submissions").then((r) => { setSubs(r.data); setSelected([]); });
  useEffect(() => { api.get("/forms/center").then((r) => setCenter(r.data)); loadSubs(); }, []);

  // Catalog-only / retired entries ("upcoming") are hidden from the primary cards by default; the full catalog is preserved and counted
  const upcoming = useMemo(() => (center ? center.entries.filter(isUpcoming) : []), [center]);
  const available = useMemo(() => (center ? center.entries.filter((e) => !isUpcoming(e)) : []), [center]);
  const shown = useMemo(() => (center ? (showUpcoming || filters.status === "catalog_only" || filters.status === "retired" ? center.entries : available) : []), [center, available, showUpcoming, filters.status]);
  const filtered = useMemo(() => applyFilters(shown, filters), [shown, filters]);
  const filtering = filters !== EMPTY_FILTERS && JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  const remove = async (id) => {
    try { await api.delete(`/forms/submissions/${id}`); toast.success("Deleted"); loadSubs(); }
    catch { toast.error("Could not delete"); }
  };
  const viewAll = (catId) => {
    setOpenCats((o) => (o.includes(catId) ? o : [...o, catId]));
    if (window.matchMedia("(max-width: 1023px)").matches) setSheetOpen(true);
    else document.getElementById("all-forms-catalog")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!center) return <div className="p-10 text-center text-slate-500">Loading Forms Center…</div>;
  const labels = center.labels;
  const catalog = <CatalogPanel entries={filtered} categories={center.categories} isManager={isManager} labels={labels} openCategory={openCats} onOpenChange={setOpenCats} />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">Forms</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E]">LUMIS HR Forms Center</h1>
          <p className="text-sm text-slate-600 mt-1">Original LUMIS HR templates, official government form links and your private drafts — organized by HR function. Prepared for use by <span className="font-semibold" data-testid="org-name">{center.org_name}</span>.
            {isManager && <OrgNameEditor current={center.org_name} onSaved={(name) => setCenter((c) => ({ ...c, org_name: name }))} />}
          </p>
          <p className="text-sm font-semibold text-[#0A2A5E] mt-2" data-testid="catalog-summary">A {center.entries.length}-form catalog, with {available.length} resources currently available and {upcoming.length} planned.</p>
          <p className="text-xs text-slate-500 mt-1" data-testid="catalog-breakdown">{["beta", "government", "existing"].map((st) => `${available.filter((e) => e.status === st).length} ${{ beta: "LUMIS HR beta templates", government: "official government-form links", existing: "existing forms" }[st]}`).join(" · ")} · {upcoming.length} planned forms</p>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="forms-tabs">
            <TabsTrigger value="center" data-testid="forms-tab-templates">Forms Center</TabsTrigger>
            <TabsTrigger value="records" data-testid="forms-tab-submissions">My Records ({subs.length})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {tab === "records" && <MyRecords subs={subs} selected={selected} setSelected={setSelected} remove={remove} userId={user?.id} />}

      {tab === "center" && (
        <>
          <p role="note" data-testid="forms-sensitive-warning" className="text-[11px] text-[#7C3F00] font-medium mb-4">{labels.sensitive}</p>
          {center.is_lumis_admin && <GovernanceStrip entries={center.entries} />}
          <div className="lg:hidden mb-4">
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button data-testid="browse-all-forms-btn" variant="outline" className="w-full h-11 border-[#0A2A5E] text-[#0A2A5E] justify-between"><span className="inline-flex items-center"><LibraryBig className="w-4 h-4 mr-2" aria-hidden="true" /> Browse All Forms ({shown.length})</span><ChevronRight className="w-4 h-4" aria-hidden="true" /></Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[85vh] overflow-y-auto" data-testid="browse-all-forms-sheet">
                <SheetHeader><SheetTitle className="font-display text-[#0A2A5E]">All Forms</SheetTitle></SheetHeader>
                <div className="mt-3 space-y-3"><CatalogFilters filters={filters} setFilters={setFilters} categories={center.categories} compact />{catalog}</div>
              </SheetContent>
            </Sheet>
          </div>

          <div className="lg:grid lg:grid-cols-[minmax(0,72%)_minmax(0,28%)] lg:gap-6">
            <div className="space-y-8" data-testid="forms-center-main">
              <div className="hidden lg:block"><CatalogFilters filters={filters} setFilters={setFilters} categories={center.categories} /></div>
              {filtering ? (
                <Card className="p-4 bg-white border-slate-200" data-testid="filter-results">
                  <div className="text-sm text-slate-600 mb-3">{filtered.length} matching form{filtered.length === 1 ? "" : "s"} <button type="button" className="ml-2 underline text-[#0A2A5E]" onClick={() => setFilters(EMPTY_FILTERS)} data-testid="clear-filters">Clear filters</button></div>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">{filtered.slice(0, 12).map((e) => <FormCard key={e.id} entry={e} isManager={isManager} labels={labels} />)}</div>
                  {filtered.length > 12 && <p className="text-xs text-slate-500 mt-3">Showing 12 of {filtered.length}. Refine the filters or use the All Forms catalog.</p>}
                </Card>
              ) : center.categories.map((c) => <CategorySection key={c.id} cat={c} entries={available} upcomingCount={upcoming.filter((e) => e.category_id === c.id).length} isManager={isManager} labels={labels} onViewAll={viewAll} />)}
              {upcoming.length > 0 && (
                <div className="pt-2 border-t border-slate-200" data-testid="upcoming-forms">
                  <Button variant="outline" size="sm" data-testid="view-upcoming-forms-btn" aria-expanded={showUpcoming} onClick={() => setShowUpcoming((v) => !v)} className="h-9 border-[#0A2A5E] text-[#0A2A5E] hover:bg-[#FDECD0]">
                    {showUpcoming ? "Hide planned forms" : `View ${upcoming.length} planned forms`}
                  </Button>
                  {showUpcoming && !filtering && <div className="mt-4"><UpcomingSection upcoming={upcoming} categories={center.categories} isManager={isManager} labels={labels} /></div>}
                </div>
              )}
            </div>
            <aside id="all-forms-catalog" className="hidden lg:block" aria-label="All Forms catalog">
              <Card className="p-4 bg-white border-slate-200 sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto" data-testid="all-forms-catalog">
                <div className="flex items-center justify-between mb-2"><h2 className="font-display font-bold text-[#0A2A5E] text-base">All Forms</h2><span className="text-xs text-slate-500" data-testid="all-forms-count">{filtered.length} shown · {center.entries.length} in catalog</span></div>
                {catalog}
              </Card>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
